/**
 * Creates an exponential backoff calculator for polling intervals
 * 
 * @param initialDelay - The initial delay in milliseconds (default: 500ms)
 * @param maxDelay - The maximum delay in milliseconds (default: 5000ms)
 * @param multiplier - The multiplier for each subsequent attempt (default: 2)
 * @returns A function that calculates the next delay based on attempt count
 */
export function createExponentialBackoff(
  initialDelay: number = 500,
  maxDelay: number = 5000,
  multiplier: number = 2
) {
  let attemptCount = 0;

  return {
    /**
     * Get the next delay interval
     */
    getNextDelay: (): number => {
      const delay = Math.min(initialDelay * Math.pow(multiplier, attemptCount), maxDelay);
      attemptCount++;
      return delay;
    },
    
    /**
     * Reset the attempt count back to 0
     */
    reset: () => {
      attemptCount = 0;
    },
    
    /**
     * Get the current attempt count
     */
    getAttemptCount: (): number => {
      return attemptCount;
    }
  };
}

/**
 * Hook-compatible exponential backoff for React Query refetchInterval
 * This returns a function that can be directly used as refetchInterval in useQuery
 * 
 * Usage:
 * ```
 * const backoff = useExponentialBackoff();
 * useQuery({
 *   queryKey: ['data'],
 *   queryFn: fetchData,
 *   refetchInterval: backoff.getInterval
 * })
 * ```
 */
export function useExponentialBackoff(
  initialDelay: number = 500,
  maxDelay: number = 5000,
  multiplier: number = 2
) {
  const backoffRef = { current: createExponentialBackoff(initialDelay, maxDelay, multiplier) };

  return {
    /**
     * Function to be passed to refetchInterval
     * React Query will call this with (data, query) but we ignore those params
     */
    getInterval: () => backoffRef.current.getNextDelay(),
    
    /**
     * Reset the backoff counter
     */
    reset: () => backoffRef.current.reset(),
    
    /**
     * Get current attempt count
     */
    getAttemptCount: () => backoffRef.current.getAttemptCount()
  };
}

/**
 * Poll for profile updates with exponential backoff
 * Continues until entitlements change or max attempts reached
 * 
 * @param queryClient - React Query client instance
 * @param currentEntitlements - Current entitlements to compare against
 * @param onUpdate - Callback when entitlements change
 * @param maxAttempts - Maximum number of polling attempts (default: 10)
 * @returns Cancel function to stop polling
 */
export async function pollForEntitlementUpdate(
  queryClient: any,
  currentEntitlements: any[],
  onUpdate?: () => void,
  maxAttempts: number = 10
): Promise<void> {
  const backoff = createExponentialBackoff(500, 5000, 2);
  const poll = async (attempt: number = 0): Promise<void> => {
    if (attempt >= maxAttempts) {
      console.log('Max polling attempts reached');
      onUpdate?.();
      return;
    }

    try {
      // Invalidate and refetch profile
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
      const profileData = queryClient.getQueryData(['profile']);

      // Check if entitlements have changed
      if (profileData?.data?.entitlements) {
        const newEntitlements = profileData.data.entitlements;
        const hasChanged = hasEntitlementsChanged(currentEntitlements, newEntitlements);

        if (hasChanged) {
          console.log('Entitlements updated successfully');
          onUpdate?.();
          return;
        }
      }

      // Schedule next poll with exponential backoff
      const delay = backoff.getNextDelay();
      setTimeout(() => poll(attempt + 1), delay);
    } catch (error) {
      console.error('Error polling for entitlement update:', error);
      // Continue polling even on error
      const delay = backoff.getNextDelay();
      setTimeout(() => poll(attempt + 1), delay);
    }
  };

  // Start polling
  await poll();
}

/**
 * Compare two entitlement arrays to detect changes
 */
function hasEntitlementsChanged(oldEntitlements: any[], newEntitlements: any[]): boolean {
  // Check if lengths differ
  if (oldEntitlements.length !== newEntitlements.length) {
    return true;
  }

  // Create maps for comparison
  const oldMap = new Map(oldEntitlements.map(e => [e.id, e]));
  const newMap = new Map(newEntitlements.map(e => [e.id, e]));

  // Check if any entitlement ID is new or removed
  for (const id of newMap.keys()) {
    if (!oldMap.has(id)) {
      return true;
    }
  }

  for (const id of oldMap.keys()) {
    if (!newMap.has(id)) {
      return true;
    }
  }

  // Check if any entitlement status or key fields changed
  for (const [id, newEnt] of newMap.entries()) {
    const oldEnt = oldMap.get(id);
    if (
      oldEnt.status !== newEnt.status ||
      oldEnt.type !== newEnt.type ||
      oldEnt.end?.toString() !== newEnt.end?.toString() ||
      oldEnt.begin?.toString() !== newEnt.begin?.toString()
    ) {
      return true;
    }
  }

  return false;
}

